import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Shield, ArrowRight, Scale, Users, FileText, Quote, Play, ExternalLink } from 'lucide-react';
import { siteConfig, aboutContent, testimonials } from '../data/mock';
import Newsletter from '../components/Newsletter';
import { MesoamericanBackground, PyramidDivider, CalendarMotif, SerpentBorder } from '../components/MesoamericanPatterns';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-stone-50" />
        <MesoamericanBackground opacity={0.05} />
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 opacity-10 hidden lg:block">
          <CalendarMotif size={150} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
              {t('about.heroTitle')}
            </h1>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              {t('about.heroSubtitle')}
            </p>
          </div>
          
          <div className="flex justify-center">
            <img 
              src={siteConfig.logo} 
              alt="Xicano Rights Union"
              className="h-40 w-auto"
            />
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="relative">
        <SerpentBorder className="bg-teal-600" />
        <div className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('about.missionTitle')}</h2>
            <p className="text-xl text-white/90 leading-relaxed">
              {t('about.missionText')}
            </p>
          </div>
        </div>
        <SerpentBorder className="bg-teal-600 rotate-180" />
      </section>

      {/* Our Story */}
      <section className="py-24 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6">
                {t('about.historyTitle')}
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>
                  {t('about.historyText')}
                </p>
                <p>
                  {aboutContent.xicanoExplanation}
                </p>
                <p>
                  {aboutContent.todayDescription}
                </p>
                <p>
                  {aboutContent.legacyStatement}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl text-center shadow-sm">
                <Scale className="text-teal-600 mx-auto mb-3" size={32} />
                <p className="text-2xl font-bold text-stone-800 mb-1">Civil Rights</p>
                <p className="text-stone-500 text-sm">Advocacy & Litigation</p>
              </div>
              <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl text-center shadow-sm">
                <Users className="text-teal-600 mx-auto mb-3" size={32} />
                <p className="text-2xl font-bold text-stone-800 mb-1">Community</p>
                <p className="text-stone-500 text-sm">Organizing & Empowerment</p>
              </div>
              <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl text-center shadow-sm">
                <FileText className="text-teal-600 mx-auto mb-3" size={32} />
                <p className="text-2xl font-bold text-stone-800 mb-1">Policy</p>
                <p className="text-stone-500 text-sm">Reform & Accountability</p>
              </div>
              <div className="p-6 bg-stone-50 border border-stone-200 rounded-xl text-center shadow-sm">
                <Shield className="text-teal-600 mx-auto mb-3" size={32} />
                <p className="text-2xl font-bold text-stone-800 mb-1">Protection</p>
                <p className="text-stone-500 text-sm">Rights & Justice</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24 relative bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="p-8 bg-white border border-stone-200 rounded-xl shadow-sm">
              <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-4">{t('about.missionTitle')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('about.missionText')}
              </p>
            </div>
            
            {/* Vision */}
            <div className="p-8 bg-white border border-stone-200 rounded-xl shadow-sm">
              <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-4">{t('about.visionTitle')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('about.visionText')}
              </p>
            </div>
            
            {/* Values */}
            <div className="p-8 bg-white border border-stone-200 rounded-xl shadow-sm">
              <div className="w-14 h-14 bg-red-500 rounded-xl flex items-center justify-center mb-6">
                <Shield className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-4">{t('about.valuesTitle')}</h3>
              <ul className="space-y-2">
                <li className="text-stone-600 text-sm leading-relaxed flex items-start gap-2">
                  <span className="text-teal-600 mt-1">•</span>
                  {t('about.values.justice.title')}: {t('about.values.justice.desc')}
                </li>
                <li className="text-stone-600 text-sm leading-relaxed flex items-start gap-2">
                  <span className="text-teal-600 mt-1">•</span>
                  {t('about.values.culture.title')}: {t('about.values.culture.desc')}
                </li>
                <li className="text-stone-600 text-sm leading-relaxed flex items-start gap-2">
                  <span className="text-teal-600 mt-1">•</span>
                  {t('about.values.community.title')}: {t('about.values.community.desc')}
                </li>
                <li className="text-stone-600 text-sm leading-relaxed flex items-start gap-2">
                  <span className="text-teal-600 mt-1">•</span>
                  {t('about.values.education.title')}: {t('about.values.education.desc')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              {t('about.howWeWork')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: t('about.workAreas.investigation.title'),
                description: t('about.workAreas.investigation.desc')
              },
              {
                title: t('about.workAreas.advocacy.title'),
                description: t('about.workAreas.advocacy.desc')
              },
              {
                title: t('about.workAreas.education.title'),
                description: t('about.workAreas.education.desc')
              },
              {
                title: t('about.workAreas.youth.title'),
                description: t('about.workAreas.youth.desc')
              },
              {
                title: t('about.workAreas.mentalHealth.title'),
                description: t('about.workAreas.mentalHealth.desc')
              },
              {
                title: t('about.workAreas.unity.title'),
                description: t('about.workAreas.unity.desc')
              }
            ].map((item, index) => (
              <div key={index} className="p-8 bg-stone-50 border border-stone-200 rounded-xl shadow-sm">
                <h3 className="text-xl font-semibold text-stone-800 mb-3">{item.title}</h3>
                <p className="text-stone-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Video Section */}
      <section className="py-24 relative bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('about.communityVideo.title')}
            </h2>
            <p className="text-lg text-stone-300 max-w-2xl mx-auto">
              {t('about.communityVideo.subtitle')}
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <video
                className="w-full aspect-video"
                controls
                poster={siteConfig.heroImage}
                preload="metadata"
              >
                <source src={siteConfig.communityVideo.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* American Homeboy Documentary Link */}
            <div className="mt-8 p-6 bg-stone-700/50 border border-stone-600 rounded-xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500/20 rounded-lg">
                    <Play className="text-amber-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{siteConfig.videoFeature.title}</h3>
                    <p className="text-stone-400 text-sm">{siteConfig.videoFeature.description}</p>
                  </div>
                </div>
                <a
                  href={siteConfig.videoFeature.tubiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {t('about.communityVideo.watchDocumentary')}
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Voices / Testimonials */}
      <section className="py-24 relative bg-amber-50">
        <MesoamericanBackground opacity={0.03} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <PyramidDivider className="mb-8" />
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              {t('about.testimonials.title')}
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              {t('about.testimonials.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8 bg-white border border-stone-200 rounded-xl shadow-sm relative"
              >
                <Quote className="absolute top-4 right-4 text-teal-200" size={40} />
                <p className="text-stone-600 leading-relaxed mb-6 italic relative z-10">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {testimonial.author.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800">{testimonial.author}</p>
                    <p className="text-stone-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />

      {/* CTA */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6">
            {t('common.joinMovement')}
          </h2>
          <p className="text-lg text-stone-600 mb-10 max-w-2xl mx-auto">
            {t('home.joinFightSubtitle')}
          </p>
          <Link
            to="/join"
            className="inline-flex items-center gap-2 px-10 py-5 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/25"
          >
            {t('programs.getInvolved')}
            <ArrowRight size={22} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
